import { useCallback, useRef, useState } from "react";
import type { Question } from "@components/Sender/QuestionItem";

/**
 * 推荐问题跟踪 Hook
 * 用于跟踪用户是否修改了推荐问题，以便在发送时决定是否传递推荐问题的相关参数
 *
 * @returns {Object} 返回跟踪相关的函数和状态
 */
export const useRecommendQuestionTracking = () => {
  // 输入框的值
  const [value, setValue] = useState("");

  // 跟踪当前选中的推荐问题
  const selectedQuestionRef = useRef<Question | null>(null);
  // 跟踪用户是否修改了输入（只要用户进行了手动输入操作，就认为修改过）
  const hasUserModifiedRef = useRef(false);
  // 跟踪是否正在选择推荐问题（用于区分系统自动触发的 onChange 和用户手动输入的 onChange）
  const isSelectingQuestionRef = useRef(false);

  /**
   * 处理输入变化
   * @param e - 输入事件
   */
  const handleChange = useCallback((e: any) => {
    const newValue = e.target?.value || "";
    setValue(newValue);

    // 如果正在选择推荐问题，这是系统自动触发的 onChange，不算用户修改
    if (isSelectingQuestionRef.current) {
      isSelectingQuestionRef.current = false;
      return;
    }

    // 如果用户已经选中了推荐问题，且这是用户手动输入的 onChange，则认为用户修改过
    // 无论最终值是否和原问题相同，只要用户进行了输入操作，就认为修改过
    if (selectedQuestionRef.current) {
      hasUserModifiedRef.current = true;
    }
  }, []);

  /**
   * 处理推荐问题选择
   * @param question - 选中的推荐问题
   */
  const handleQuestionSelect = useCallback((question: Question) => {
    selectedQuestionRef.current = question;
    hasUserModifiedRef.current = false; // 重置修改状态
    isSelectingQuestionRef.current = true; // 标记正在选择推荐问题，接下来的 onChange 是系统自动触发的
  }, []);

  /**
   * 判断是否应该使用推荐问题信息
   * @param currentQuestion - 当前输入框的问题文本
   * @returns 是否应该传递推荐问题的相关参数
   */
  const shouldUseRecommendInfo = useCallback(
    (currentQuestion: string | undefined) => {
      const selectedQuestion = selectedQuestionRef.current;
      const hasUserModified = hasUserModifiedRef.current;

      return (
        selectedQuestion &&
        !hasUserModified &&
        currentQuestion === selectedQuestion.question &&
        !!selectedQuestion.source_id
      );
    },
    [],
  );

  /**
   * 获取当前选中的推荐问题信息
   * @returns 选中的推荐问题，如果没有则返回 null
   */
  const getRecommendInfo = useCallback(() => {
    return selectedQuestionRef.current;
  }, []);

  /**
   * 重置所有状态
   */
  const reset = useCallback(() => {
    setValue("");
    selectedQuestionRef.current = null;
    hasUserModifiedRef.current = false;
    isSelectingQuestionRef.current = false;
  }, []);

  return {
    value,
    setValue,
    handleChange,
    handleQuestionSelect,
    shouldUseRecommendInfo,
    getRecommendInfo,
    reset,
  };
};
